<template>
  <!-- Desktop: pill filters (NcrmFilterPills) -->
  <NcrmFilterPills
    v-if="layout === 'bar'"
    class="ncrm-activities-filters ncrm-activities-filters--bar"
    :filters="barFilters"
    :count-label="$t('ncrm.activities.countLabel', { count })"
    :clear-label="$t('ncrm.activities.actions.clearFilters')"
    :has-active-filters="hasActiveFilters"
    :active-filter-count="activeFilterCount"
    @update="$emit('update', $event)"
    @clear="$emit('clear')"
  />

  <!-- Mobile bottom sheet: stacked select filters -->
  <div v-else class="ncrm-activities-filters ncrm-activities-filters--sheet">
    <v-select
      :model-value="selectedEntityType"
      :items="entityTypeOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.activities.filters.entityType')"
      hide-details
      clearable
      :class="['ncrm-activities-filters__field', { 'ncrm-filter--active': activeFilters.entityType }]"
      @update:model-value="emitUpdate('selectedEntityType', $event ?? '')"
    />

    <v-autocomplete
      :model-value="ownerUserIdFilter"
      :items="ownerOptions"
      item-title="label"
      item-value="id"
      :label="$t('ncrm.activities.filters.owner')"
      :loading="ownerOptionsLoading"
      hide-details
      clearable
      :class="['ncrm-activities-filters__field', { 'ncrm-filter--active': activeFilters.owner }]"
      @update:model-value="emitUpdate('ownerUserIdFilter', $event ?? null)"
    />

    <v-select
      :model-value="selectedType"
      :items="activityTypeOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.activities.filters.type')"
      hide-details
      clearable
      :class="['ncrm-activities-filters__field', { 'ncrm-filter--active': activeFilters.type }]"
      @update:model-value="emitUpdate('selectedType', $event ?? '')"
    />

    <v-select
      :model-value="selectedStatus"
      :items="statusOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.activities.filters.status')"
      hide-details
      clearable
      :class="['ncrm-activities-filters__field', { 'ncrm-filter--active': activeFilters.status }]"
      @update:model-value="emitUpdate('selectedStatus', $event ?? '')"
    />

    <v-select
      :model-value="assignmentSourceFilter"
      :items="assignmentSourceOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.activities.filters.assignmentSource')"
      hide-details
      clearable
      :class="['ncrm-activities-filters__field', { 'ncrm-filter--active': activeFilters.assignmentSource }]"
      @update:model-value="emitUpdate('assignmentSourceFilter', $event ?? '')"
    />

    <v-text-field
      :model-value="entityNameInput"
      :label="$t('ncrm.activities.filters.entityName')"
      hide-details
      :class="['ncrm-activities-filters__field', { 'ncrm-filter--active': activeFilters.entityName }]"
      @update:model-value="emitUpdate('entityNameInput', $event ?? '')"
    />

    <v-select
      :model-value="overdueFilter"
      :items="overdueOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.activities.filters.overdue')"
      hide-details
      :class="['ncrm-activities-filters__field', { 'ncrm-filter--active': activeFilters.overdue }]"
      @update:model-value="emitUpdate('overdueFilter', $event ?? '')"
    />

    <v-text-field
      :model-value="dueDateInput"
      :label="$t('ncrm.activities.filters.dueDate')"
      type="date"
      hide-details
      :class="['ncrm-activities-filters__field', { 'ncrm-filter--active': activeFilters.dueDate }]"
      @update:model-value="emitUpdate('dueDateInput', $event ?? '')"
    />

    <div class="ncrm-activities-filters__sheet-actions">
      <v-btn
        block
        variant="tonal"
        color="primary"
        :disabled="!hasActiveFilters"
        @click="$emit('clear')"
      >
        <v-icon start size="18">mdi-filter-remove-outline</v-icon>
        {{ $t("ncrm.activities.actions.clearFilters") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import NcrmFilterPills from "@/components/ncrm/shared/NcrmFilterPills.vue";

export default {
  name: "NcrmActivitiesFilters",
  components: { NcrmFilterPills },
  props: {
    layout: { type: String, default: "bar" },
    selectedEntityType: { type: String, default: "" },
    ownerUserIdFilter: { type: [String, Number], default: null },
    selectedType: { type: String, default: "" },
    selectedStatus: { type: String, default: "" },
    assignmentSourceFilter: { type: String, default: "" },
    entityNameInput: { type: String, default: "" },
    overdueFilter: { type: String, default: "" },
    dueDateInput: { type: String, default: "" },
    entityTypeOptions: { type: Array, default: () => [] },
    ownerOptions: { type: Array, default: () => [] },
    ownerOptionsLoading: { type: Boolean, default: false },
    activityTypeOptions: { type: Array, default: () => [] },
    statusOptions: { type: Array, default: () => [] },
    assignmentSourceOptions: { type: Array, default: () => [] },
    overdueOptions: { type: Array, default: () => [] },
    activeFilters: { type: Object, default: () => ({}) },
    activeFilterCount: { type: Number, default: 0 },
    hasActiveFilters: { type: Boolean, default: false },
    count: { type: Number, default: 0 },
  },
  emits: ["update", "clear"],
  computed: {
    barFilters() {
      const labelFor = (options, value, key = "value") => {
        const found = (options || []).find((opt) => String(opt[key]) === String(value));
        return found ? found.label : String(value ?? "");
      };
      return [
        {
          field: "selectedEntityType",
          type: "select",
          label: this.$t("ncrm.activities.filters.entityType"),
          options: this.entityTypeOptions,
          value: this.selectedEntityType,
          active: this.activeFilters.entityType,
          display: labelFor(this.entityTypeOptions, this.selectedEntityType),
          clearValue: "",
        },
        {
          field: "ownerUserIdFilter",
          type: "autocomplete",
          label: this.$t("ncrm.activities.filters.owner"),
          options: this.ownerOptions,
          value: this.ownerUserIdFilter,
          active: this.activeFilters.owner,
          display: labelFor(this.ownerOptions, this.ownerUserIdFilter, "id"),
          clearValue: null,
          loading: this.ownerOptionsLoading,
        },
        {
          field: "selectedType",
          type: "select",
          label: this.$t("ncrm.activities.filters.type"),
          options: this.activityTypeOptions,
          value: this.selectedType,
          active: this.activeFilters.type,
          display: labelFor(this.activityTypeOptions, this.selectedType),
          clearValue: "",
        },
        {
          field: "selectedStatus",
          type: "select",
          label: this.$t("ncrm.activities.filters.status"),
          options: this.statusOptions,
          value: this.selectedStatus,
          active: this.activeFilters.status,
          display: labelFor(this.statusOptions, this.selectedStatus),
          clearValue: "",
        },
        {
          field: "assignmentSourceFilter",
          type: "select",
          label: this.$t("ncrm.activities.filters.assignmentSource"),
          options: this.assignmentSourceOptions,
          value: this.assignmentSourceFilter,
          active: this.activeFilters.assignmentSource,
          display: labelFor(this.assignmentSourceOptions, this.assignmentSourceFilter),
          clearValue: "",
        },
        {
          field: "entityNameInput",
          type: "text",
          label: this.$t("ncrm.activities.filters.entityName"),
          value: this.entityNameInput,
          active: this.activeFilters.entityName,
          display: this.entityNameInput,
          clearValue: "",
        },
        {
          field: "overdueFilter",
          type: "select",
          label: this.$t("ncrm.activities.filters.overdue"),
          options: this.overdueOptions,
          value: this.overdueFilter,
          active: this.activeFilters.overdue,
          display: labelFor(this.overdueOptions, this.overdueFilter),
          clearValue: "",
        },
        {
          field: "dueDateInput",
          type: "date",
          label: this.$t("ncrm.activities.filters.dueDate"),
          value: this.dueDateInput,
          active: this.activeFilters.dueDate,
          display: this.dueDateInput,
          clearValue: "",
        },
      ];
    },
  },
  methods: {
    emitUpdate(field, value) {
      this.$emit("update", { field, value });
    },
  },
};
</script>

<style scoped>
/* ── Mobile sheet ─────────────────────────────────────────────── */
.ncrm-activities-filters--sheet {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ncrm-activities-filters--sheet .ncrm-activities-filters__field {
  width: 100%;
}

.ncrm-activities-filters__sheet-actions {
  margin-top: 2px;
}

:deep(.ncrm-filter--active .v-field) {
  background-color: rgba(var(--v-theme-primary), 0.1) !important;
}

:deep(.ncrm-filter--active .v-field__outline) {
  --v-field-border-opacity: 1;
  color: rgb(var(--v-theme-primary)) !important;
}

:deep(.ncrm-filter--active .v-field-label),
:deep(.ncrm-filter--active .v-field .v-icon) {
  color: rgb(var(--v-theme-primary)) !important;
}
</style>
